import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Space,
  message,
  Popconfirm,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { DictService } from "../../services";
import {
  Dict,
  CreateDictDto,
  UpdateDictDto,
  DictQueryParams,
} from "../../types/dict";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import "./DictManagement.scss";

const DictManagementPage: React.FC = () => {
  const [dicts, setDicts] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDict, setEditingDict] = useState<Dict | null>(null);
  const [searchParams, setSearchParams] = useState<DictQueryParams>({});
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 10,
    total: 0,
  });
  const [dictForm] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 加载字典列表
  const loadDicts = async (params?: DictQueryParams) => {
    setLoading(true);
    try {
      const queryParams = {
        ...searchParams,
        ...params,
        page: pagination.current,
        limit: pagination.limit,
      };

      const response = await DictService.getDictList(queryParams);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setDicts(data.list || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取字典列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 使用独立参数加载字典，避免状态合并问题
  const loadDictsWithParams = async (params: any) => {
    setLoading(true);
    try {
      const response = await DictService.getDictList(params);

      if (response.code === 0 && response.data) {
        const data = response.data;
        setDicts(data.list || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取字典列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadDicts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 保存字典
  const handleSaveDict = async (values: CreateDictDto | UpdateDictDto) => {
    try {
      setLoading(true);
      let response;

      if (editingDict) {
        // 更新
        response = await DictService.updateDict(editingDict.id, values);
      } else {
        // 创建
        response = await DictService.createDict(values as CreateDictDto);
      }

      if (response.code === 0) {
        message.success(`${editingDict ? "更新" : "创建"}字典成功`);
        handleCloseModal();
        loadDicts();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开字典弹窗
  const handleOpenModal = (dict?: Dict) => {
    setEditingDict(dict || null);
    // 先重置表单，确保干净的状态
    dictForm.resetFields();
    setModalVisible(true);

    // 如果是编辑模式，在下一个事件循环中设置值，确保重置完成
    if (dict) {
      setTimeout(() => {
        dictForm.setFieldsValue(dict);
      }, 0);
    }
  };

  // 关闭字典弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingDict(null);
    // 关闭时也重置表单
    dictForm.resetFields();
  };

  // 删除字典
  const handleDeleteDict = async (id: number) => {
    try {
      setLoading(true);
      const response = await DictService.deleteDict(id);

      if (response.code === 0) {
        message.success("删除字典成功");
        loadDicts();
      } else {
        message.error(response.msg || "删除失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索字典
  const handleSearch = async (values: DictQueryParams) => {
    const params = {
      ...values,
      page: 1, // 搜索时重置到第一页
    };
    setSearchParams(params);
    setPagination((prev) => ({ ...prev, current: 1 }));
    await loadDicts(params);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    const resetParams = {};
    setSearchParams(resetParams);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 直接传入重置后的参数，包括页码重置为1
    loadDictsWithParams({ page: 1, limit: pagination.limit });
  };

  // 字典表格列
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "字典类型",
      dataIndex: "dictType",
      key: "dictType",
      width: 120,
    },
    {
      title: "字典键",
      dataIndex: "dictKey",
      key: "dictKey",
      width: 120,
    },
    {
      title: "字典值",
      dataIndex: "dictValue",
      key: "dictValue",
      width: 120,
      render: (text: string) => (
        <div
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            maxWidth: "120px",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "中文名称",
      dataIndex: "dictCnName",
      key: "dictCnName",
      width: 150,
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
      width: 180,
      render: (text: string) => text || "-",
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
    },
    {
      title: "操作",
      key: "action",
      width: 120,
      render: (text: any, record: Dict) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个字典吗？"
            onConfirm={() => handleDeleteDict(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="字典管理">
      <div className="dict-management">
        {/* 搜索区域 */}
        <Card className="search-card" style={{ marginBottom: 16 }}>
          <Form form={searchForm} layout="inline" onFinish={handleSearch}>
            <Form.Item name="dictType" label="字典类型">
              <Input placeholder="请输入字典类型" style={{ width: 200 }} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </Button>
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 字典列表 */}
        <Card
          title="字典列表"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              新增字典
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={dicts}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  limit: pageSize || 10,
                }));
                loadDicts({ page, limit: pageSize || 10 });
              },
            }}
          />
        </Card>

        {/* 字典弹窗 */}
        <Modal
          title={`${editingDict ? "编辑" : "新增"}字典`}
          open={modalVisible}
          onOk={() => dictForm.submit()}
          onCancel={handleCloseModal}
          confirmLoading={loading}
          width={600}
        >
          <Form form={dictForm} layout="vertical" onFinish={handleSaveDict}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dictType"
                  label="字典类型"
                  rules={[{ required: true, message: "字典类型不能为空" }]}
                >
                  <Input placeholder="例如：system" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="dictKey"
                  label="字典键"
                  rules={[{ required: true, message: "字典键不能为空" }]}
                >
                  <Input placeholder="例如：new_user_token_gift" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dictValue"
                  label="字典值"
                  rules={[{ required: true, message: "字典值不能为空" }]}
                >
                  <Input placeholder="例如：500" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="dictCnName"
                  label="字典中文名称"
                  rules={[{ required: true, message: "字典中文名称不能为空" }]}
                >
                  <Input placeholder="例如：新客赠送token" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="remark" label="备注">
                  <Input.TextArea placeholder="请输入备注（可选）" rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default DictManagementPage;
